import { useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText, AlertCircle, CheckCircle, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function TermsOfService() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b bg-card/50 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/')}
            className="mb-2"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Button>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10">
              <FileText className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Terms of Service</h1>
              <p className="text-sm text-muted-foreground">Last updated: October 20, 2025</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8 space-y-8">
        {/* Introduction */}
        <section className="space-y-4">
          <p className="text-muted-foreground">
            Welcome to UniSchedule. By accessing or using our platform, you agree to be bound by these Terms of Service. Please read them carefully.
          </p>
        </section>

        {/* Acceptance of Terms */}
        <section className="space-y-4">
          <h2 className="text-xl font-semibold">1. Acceptance of Terms</h2>
          <p className="text-muted-foreground pl-7">
            By using UniSchedule, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service and our Privacy Policy. If you do not agree, please do not use our services.
          </p>
        </section>

        {/* Service Description */}
        <section className="space-y-4">
          <h2 className="text-xl font-semibold">2. Service Description</h2>
          <div className="space-y-3 text-muted-foreground pl-7">
            <p>UniSchedule provides:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>Personal timetable management for students</li>
              <li>Class schedule viewing for teachers</li>
              <li>Administrative tools for managing timetables and resources</li>
              <li>Campus navigation and bus schedule information</li>
              <li>Exam timetables and academic calendar</li>
            </ul>
          </div>
        </section>

        {/* User Responsibilities */}
        <section className="space-y-4">
          <div className="flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-green-500" />
            <h2 className="text-xl font-semibold">3. User Responsibilities</h2>
          </div>
          
          <div className="space-y-3 text-muted-foreground pl-7">
            <p>You agree to:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>Provide accurate information when using the platform</li>
              <li>Use the service only for lawful purposes</li>
              <li>Not attempt to gain unauthorized access to any part of the system</li>
              <li>Not interfere with or disrupt the service</li>
              <li>Keep your login credentials secure (for teachers and admins)</li>
            </ul>
          </div>
        </section>

        {/* Prohibited Activities */}
        <section className="space-y-4">
          <div className="flex items-center gap-2">
            <XCircle className="w-5 h-5 text-red-500" />
            <h2 className="text-xl font-semibold">4. Prohibited Activities</h2>
          </div>
          
          <div className="space-y-3 text-muted-foreground pl-7">
            <p>You may not:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>Use the service to transmit harmful or malicious code</li>
              <li>Attempt to reverse engineer or decompile the platform</li>
              <li>Scrape or harvest data from the service</li>
              <li>Impersonate another user or provide false information</li>
              <li>Use the service for commercial purposes without permission</li>
            </ul>
          </div>
        </section>

        {/* Intellectual Property */}
        <section className="space-y-4">
          <h2 className="text-xl font-semibold">5. Intellectual Property</h2>
          <p className="text-muted-foreground pl-7">
            All content, features, and functionality of UniSchedule are owned by us and are protected by copyright, trademark, and other intellectual property laws. You may not copy, modify, or distribute any part of the service without our permission.
          </p>
        </section>

        {/* Disclaimer of Warranties */}
        <section className="space-y-4">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-yellow-500" />
            <h2 className="text-xl font-semibold">6. Disclaimer of Warranties</h2>
          </div>
          
          <div className="space-y-3 text-muted-foreground pl-7">
            <p>
              UniSchedule is provided "as is" without warranties of any kind. We do not guarantee that:
            </p>
            <ul className="list-disc list-inside space-y-1">
              <li>The service will be uninterrupted or error-free</li>
              <li>All information displayed is accurate or up-to-date</li>
              <li>The service will meet your specific requirements</li>
            </ul>
            <p className="text-sm mt-3">
              Always verify critical information (exam times, room locations) with official university sources.
            </p>
          </div>
        </section>

        {/* Limitation of Liability */}
        <section className="space-y-4">
          <h2 className="text-xl font-semibold">7. Limitation of Liability</h2>
          <p className="text-muted-foreground pl-7">
            To the maximum extent permitted by law, UniSchedule shall not be liable for any indirect, incidental, special, or consequential damages arising from your use of the service, including but not limited to missed classes, incorrect room locations, or schedule conflicts.
          </p>
        </section>

        {/* Data and Privacy */}
        <section className="space-y-4">
          <h2 className="text-xl font-semibold">8. Data and Privacy</h2>
          <p className="text-muted-foreground pl-7">
            Your use of UniSchedule is also governed by our Privacy Policy. We store minimal data locally on your device and do not sell your information to third parties.
          </p>
        </section>

        {/* Modifications to Service */}
        <section className="space-y-4">
          <h2 className="text-xl font-semibold">9. Modifications to Service</h2>
          <p className="text-muted-foreground pl-7">
            We reserve the right to modify, suspend, or discontinue any part of the service at any time without notice. We may also update these Terms of Service periodically.
          </p>
        </section>

        {/* Termination */}
        <section className="space-y-4">
          <h2 className="text-xl font-semibold">10. Termination</h2>
          <p className="text-muted-foreground pl-7">
            We may terminate or suspend your access to the service immediately, without prior notice, for any breach of these Terms of Service.
          </p>
        </section>

        {/* Governing Law */}
        <section className="space-y-4">
          <h2 className="text-xl font-semibold">11. Governing Law</h2>
          <p className="text-muted-foreground pl-7">
            These Terms shall be governed by and construed in accordance with applicable laws, without regard to conflict of law provisions.
          </p>
        </section>

        {/* Contact */}
        <section className="space-y-4">
          <h2 className="text-xl font-semibold">12. Contact Information</h2>
          <p className="text-muted-foreground pl-7">
            For questions about these Terms of Service, please contact us at:{' '}
            <a href="mailto:faisalh5556@gmail.com" className="text-primary hover:underline">
              faisalh5556@gmail.com
            </a>
          </p>
        </section>

        {/* Back Button */}
        <div className="pt-8 border-t">
          <Button onClick={() => navigate('/')} variant="outline">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Button>
        </div>
      </div>
    </div>
  );
}
